// src/pages/tenant/TenantQuotasPage.tsx
// Tenant quotas and usage overview with visual progress bars and admin editing.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format byte count to human-readable string. */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value % 1 === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

/** Calculate usage percentage (0-100). Returns 0 if limit is 0 (unlimited). */
function usagePercent(current: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(Math.round((current / max) * 100), 100);
}

/** Color class for progress bar based on percentage. */
function usageColor(percent: number): string {
  if (percent > 90) return 'bg-red-500';
  if (percent > 70) return 'bg-yellow-500';
  return 'bg-green-500';
}

/** Color class for percentage text. */
function usageTextColor(percent: number): string {
  if (percent > 90) return 'text-red-600 dark:text-red-400';
  if (percent > 70) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
}

/** Format a number with comma separators. */
function formatNumber(n: number): string {
  return n.toLocaleString();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface QuotaCardProps {
  icon: React.ReactNode;
  label: string;
  current: number;
  max: number;
  formatValue?: (v: number) => string;
  t: (k: string, opts?: any) => string;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function QuotaCard({ icon, label, current, max, formatValue, t }: QuotaCardProps) {
  const format = formatValue ?? formatNumber;
  const percent = usagePercent(current, max);
  const isUnlimited = max === 0;

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{label}</p>
            {isUnlimited ? (
              <p className="text-xs text-muted-foreground mt-0.5">
                {format(current)} {t('tenant.quotas.used')} ({t('tenant.quotas.unlimited')})
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5">
                {format(current)} {t('tenant.quotas.of')} {format(max)} {t('tenant.quotas.used')}
              </p>
            )}
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${isUnlimited ? 'bg-green-500' : usageColor(percent)}`}
            style={{ width: isUnlimited ? '0%' : `${percent}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          {isUnlimited ? (
            <span className="text-xs text-muted-foreground">{t('tenant.quotas.noLimit')}</span>
          ) : (
            <>
              <span className={`text-xs font-medium ${usageTextColor(percent)}`}>
                {percent}%
              </span>
              {percent > 90 && (
                <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                  {t('tenant.quotas.nearLimit')}
                </span>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function UsersIcon() {
  return (
    <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function TeamsIcon() {
  return (
    <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  );
}

function StorageIcon() {
  return (
    <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
    </svg>
  );
}

function ApiIcon() {
  return (
    <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>
  );
}

function AppsIcon() {
  return (
    <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  );
}

function WebhooksIcon() {
  return (
    <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * TenantQuotasPage - view quotas and current usage with visual progress bars.
 * Route: /tenants/:id/settings/quotas
 */
export const TenantQuotasPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTenant();
  const { t } = useI18n();

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editValues, setEditValues] = useState({
    max_users: 0,
    max_teams: 0,
    max_storage_bytes: 0,
    max_api_calls_per_month: 0,
    max_apps: 0,
    max_webhooks: 0,
  });

  const [quota, setQuota] = useState<any>(null);
  const tenant = store.currentItem;

  useEffect(() => {
    if (id) {
      store.GetTenantQuota(id).then((res: any) => {
        setQuota(res?.limits ?? null);
      }).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    if (quota) {
      setEditValues({
        max_users: quota.max_users ?? 0,
        max_teams: quota.max_teams ?? 0,
        max_storage_bytes: quota.max_storage_bytes ?? 0,
        max_api_calls_per_month: quota.max_api_calls_per_month ?? 0,
        max_apps: quota.max_apps ?? 0,
        max_webhooks: quota.max_webhooks ?? 0,
      });
    }
  }, [quota]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const res: any = await store.UpdateTenantQuota(id, {
        limits: { ...quota, ...editValues },
      });
      setQuota(res?.limits ?? { ...quota, ...editValues });
      setSaved(true);
      setEditMode(false);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // Error handled by store
    } finally {
      setSaving(false);
    }
  };

  const handleEditValueChange = (field: string, value: string) => {
    const numValue = parseInt(value, 10);
    setEditValues((prev) => ({
      ...prev,
      [field]: isNaN(numValue) ? 0 : numValue,
    }));
  };

  const tenantName = tenant?.name ?? tenant?.slug ?? id ?? '';

  // ---------------------------------------------------------------------------
  // States
  // ---------------------------------------------------------------------------

  if (store.isLoading && !quota) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.list')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}`} className="text-muted-foreground hover:text-foreground">
          {tenantName}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/tenants/${id}/settings`} className="text-muted-foreground hover:text-foreground">
          {t('tenant.settings')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('tenant.quotas.title')}</span>
      </nav>

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('tenant.quotas.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('tenant.quotas.subtitle')}
          </p>
        </div>
      </div>

      {/* Success notification */}
      {saved && (
        <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>
          {t('tenant.quotas.saveSuccess')}
        </Alert>
      )}

      {/* Error */}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Quota overview cards - 2x3 grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <QuotaCard
          icon={<UsersIcon />}
          label={t('tenant.quotas.users')}
          current={quota?.current_users ?? 0}
          max={quota?.max_users ?? 0}
          t={t}
        />
        <QuotaCard
          icon={<TeamsIcon />}
          label={t('tenant.quotas.teams')}
          current={quota?.current_teams ?? 0}
          max={quota?.max_teams ?? 0}
          t={t}
        />
        <QuotaCard
          icon={<StorageIcon />}
          label={t('tenant.quotas.storage')}
          current={quota?.current_storage_bytes ?? 0}
          max={quota?.max_storage_bytes ?? 0}
          formatValue={formatBytes}
          t={t}
        />
        <QuotaCard
          icon={<ApiIcon />}
          label={t('tenant.quotas.apiCalls')}
          current={quota?.current_api_calls_this_month ?? 0}
          max={quota?.max_api_calls_per_month ?? 0}
          t={t}
        />
        <QuotaCard
          icon={<AppsIcon />}
          label={t('tenant.quotas.apps')}
          current={quota?.current_apps ?? 0}
          max={quota?.max_apps ?? 0}
          t={t}
        />
        <QuotaCard
          icon={<WebhooksIcon />}
          label={t('tenant.quotas.webhooks')}
          current={0}
          max={quota?.max_webhooks ?? 0}
          t={t}
        />
      </div>

      {/* Admin: Edit Quotas */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('tenant.quotas.editTitle')}</CardTitle>
              <CardDescription>{t('tenant.quotas.editDescription')}</CardDescription>
            </div>
            {!editMode && (
              <Button variant="secondary" onClick={() => setEditMode(true)}>
                {t('tenant.quotas.editButton')}
              </Button>
            )}
          </div>
        </CardHeader>
        {editMode && (
          <>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {t('tenant.quotas.users')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={String(editValues.max_users)}
                    onChange={(e) => handleEditValueChange('max_users', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{t('tenant.quotas.zeroUnlimited')}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {t('tenant.quotas.teams')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={String(editValues.max_teams)}
                    onChange={(e) => handleEditValueChange('max_teams', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{t('tenant.quotas.zeroUnlimited')}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {t('tenant.quotas.storage')} ({t('tenant.quotas.bytes')})
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={String(editValues.max_storage_bytes)}
                    onChange={(e) => handleEditValueChange('max_storage_bytes', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {editValues.max_storage_bytes > 0
                      ? formatBytes(editValues.max_storage_bytes)
                      : t('tenant.quotas.unlimited')}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {t('tenant.quotas.apiCalls')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={String(editValues.max_api_calls_per_month)}
                    onChange={(e) => handleEditValueChange('max_api_calls_per_month', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{t('tenant.quotas.zeroUnlimited')}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {t('tenant.quotas.apps')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={String(editValues.max_apps)}
                    onChange={(e) => handleEditValueChange('max_apps', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{t('tenant.quotas.zeroUnlimited')}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">
                    {t('tenant.quotas.webhooks')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={String(editValues.max_webhooks)}
                    onChange={(e) => handleEditValueChange('max_webhooks', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">{t('tenant.quotas.zeroUnlimited')}</p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex items-center gap-3 justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditMode(false);
                  if (quota) {
                    setEditValues({
                      max_users: quota.max_users ?? 0,
                      max_teams: quota.max_teams ?? 0,
                      max_storage_bytes: quota.max_storage_bytes ?? 0,
                      max_api_calls_per_month: quota.max_api_calls_per_month ?? 0,
                      max_apps: quota.max_apps ?? 0,
                      max_webhooks: quota.max_webhooks ?? 0,
                    });
                  }
                }}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? t('common.loading') : t('common.save')}
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
});

export default TenantQuotasPage;
